/**
 * Complete GTD Processing Command
 * 
 * Implements David Allen's full Getting Things Done methodology:
 * 1. Capture (already done - item is in inbox)
 * 2. Clarify (what is it? is it actionable?)
 * 3. Organize (create NextActions, Projects, Reference, or Someday/Maybe)
 * 4. Reflect (maintain system integrity) 
 * 5. Engage (choose actions based on context and energy)
 * 
 * This command bridges steps 2-3, completing the missing workflow
 * that converts inbox items into actionable work items.
 */
export interface CompleteGTDProcessingCommand {
    // Core identification
    itemId: string;
    processedByPersonId: string;
    
    // GTD Clarification (Step 2)
    clarification: string;
    isActionable: boolean;
    
    // GTD Organization (Step 3) - Action Properties
    estimatedMinutes?: number;      // GTD: If ≤2 minutes, do now
    context?: string;               // @calls, @computer, @errands, @home, etc.
    energyLevel?: string;           // high, medium, low (for energy-based selection)
    nextActionDescription?: string; // Specific, physical action description
    
    // GTD Organization (Step 3) - Project Properties  
    isProject?: boolean;            // Multi-step outcome requiring project
    projectOutcome?: string;        // Desired end result for project
    
    // GTD Organization (Step 3) - Non-actionable Properties
    isReference?: boolean;          // Useful information to keep
    isSomedayMaybe?: boolean;      // Potentially actionable in future
}

/**
 * Result of complete GTD processing
 */
export interface CompleteGTDProcessingResult {
    success: boolean;
    itemId: string;
    processedAt?: string;
    
    // GTD Clarification Results
    clarification?: string;
    isActionable?: boolean;
    workflowType?: 'actionable' | 'project' | 'non_actionable' | 'trash';
    
    // GTD Organization Results
    createdWorkItems?: Array<{
        type: 'next_action' | 'project' | 'reference_material' | 'someday_maybe';
        id: string;
        description: string;
        status: string;
        urgent?: boolean;
        reason?: string;
    }>;
    
    // Operation Results
    message: string;
    error?: string;
    
    // GTD System Statistics (helpful for dashboard)
    gtdMetrics?: {
        totalInboxItems: number;
        processedToday: number;
        nextActionsCreated: number;
        projectsCreated: number;
        referencesStored: number;
        somedayItems: number;
    };
}

/**
 * Command Validation Helpers
 */
export class CompleteGTDProcessingCommandValidator {
    
    static validate(command: CompleteGTDProcessingCommand): ValidationResult {
        const errors: string[] = [];
        
        // Required fields
        if (!command.itemId?.trim()) {
            errors.push('Item ID is required');
        }
        
        if (!command.processedByPersonId?.trim()) {
            errors.push('Processed by person ID is required');
        }
        
        if (!command.clarification?.trim()) {
            errors.push('Clarification is required for GTD processing');
        }
        
        if (command.isActionable === undefined || command.isActionable === null) {
            errors.push('Actionable decision is required (true/false)');
        }
        
        // GTD-specific validations
        if (command.isActionable) {
            // Actionable items should have action context
            if (command.estimatedMinutes && command.estimatedMinutes > 120) {
                errors.push('Actions over 2 hours should be broken into smaller steps');
            }
            
            if (command.isProject && !command.projectOutcome?.trim()) {
                errors.push('Project outcome is required for multi-step items');
            }
        } else {
            // Non-actionable items should specify disposal method
            const hasDisposalMethod = command.isReference || command.isSomedayMaybe;
            if (!hasDisposalMethod) {
                errors.push('Non-actionable items must specify reference or someday/maybe');
            }
        }
        
        // Context validation
        if (command.context) {
            const validContexts = ['@calls', '@computer', '@errands', '@home', '@office', '@anywhere'];
            const isValidContext = validContexts.includes(command.context.toLowerCase()) || 
                                 command.context.startsWith('@');
            
            if (!isValidContext) {
                errors.push('Context must start with @ (e.g., @calls, @computer)');
            }
        }
        
        // Energy level validation
        if (command.energyLevel) {
            const validEnergyLevels = ['high', 'medium', 'low'];
            if (!validEnergyLevels.includes(command.energyLevel.toLowerCase())) {
                errors.push('Energy level must be high, medium, or low');
            }
        }
        
        return {
            isValid: errors.length === 0,
            errors
        };
    }
}

interface ValidationResult {
    isValid: boolean;
    errors: string[];
}

/**
 * GTD Context Constants
 * Based on David Allen's recommended contexts
 */
export const GTD_CONTEXTS = {
    CALLS: '@calls',
    COMPUTER: '@computer', 
    ERRANDS: '@errands',
    HOME: '@home',
    OFFICE: '@office',
    ANYWHERE: '@anywhere',
    WAITING: '@waiting_for',
    READ_REVIEW: '@read_review'
} as const;

/**
 * GTD Energy Levels
 * For matching actions to available mental/physical energy
 */
export const GTD_ENERGY_LEVELS = {
    HIGH: 'high',      // Creative work, complex problem-solving
    MEDIUM: 'medium',  // Routine tasks, administrative work
    LOW: 'low'         // Mindless tasks, filing, organizing
} as const;